import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SgPieChartSection } from './sg-pie-chart-section.model';
import { sgPieChartAnimations } from './sg-pie-chart-animations';

const GAP_PIXELS = 4;

@Component({
  selector: 'sg-pie-chart',
  templateUrl: './sg-pie-chart.component.html',
  styleUrls: ['./sg-pie-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: sgPieChartAnimations,
  standalone: false,
})
export class SgPieChartComponent implements OnChanges {
  @Input() sections: SgPieChartSection[] = [];
  @Input() isLoading = false;
  @Input() size = 100;
  @Input() holeSizePercentage = 75;
  @Input() railColor = 'var(--empty-state-surface)';
  @Input() sectionExtensionPercentage = 15;

  readonly glowFilterId = `glow-${Math.random().toString(36).substr(2, 9)}`;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections'] && this.sections) {
      this.validateSections();
    }
  }

  getSvgPaths(): { d: string; color: string; glow?: boolean }[] {
    // Calculate extension based on configurable parameter
    // Extension is split equally: half inward, half outward
    const halfExtension = this.sectionExtensionPercentage / 2 / 100;

    // Calculate base radius to ensure sections don't overflow the dynamic viewBox
    // Maximum radius after extension should not exceed half the viewBox size
    const maxAllowedRadius = this.size / 2;
    const baseOuterRadius = maxAllowedRadius / (1 + halfExtension);
    const baseInnerRadius = (this.holeSizePercentage / 100) * baseOuterRadius;

    const sectionOuterRadius = baseOuterRadius * (1 + halfExtension);
    const sectionInnerRadius = baseInnerRadius * (1 - halfExtension);

    // Rail uses base radii (no extension)
    const railOuterRadius = baseOuterRadius;
    const railInnerRadius = baseInnerRadius;

    // If no sections or all sections have 0 values, show only the rail (empty state)
    if (!this.sections?.length || this.getTotalSectionValue(this.sections) === 0) {
      return [
        {
          d: this.createDonutPath(0, 360, railOuterRadius, railInnerRadius),
          color: this.railColor,
        },
      ];
    }

    // Calculate uniform gap based on straight-line distance
    const paths: { d: string; color: string; glow?: boolean }[] = [];
    let cumulativePercentage = 0;

    this.sections.forEach((section, index) => {
      if (section.value <= 0) {
        return;
      }

      const startAngle = (cumulativePercentage / 100) * 360;
      const endAngle = ((cumulativePercentage + section.value) / 100) * 360;

      // Calculate gap angle based on outer radius (this determines the visual gap size)
      const gapAngle = this.calculateUniformGapAngle(GAP_PIXELS, sectionOuterRadius);

      // Apply half gap at start and end for consistent spacing at all meeting points
      const halfGap = gapAngle / 2;

      const adjustedStartAngle = startAngle + halfGap;
      const adjustedEndAngle = endAngle - halfGap;

      if (adjustedEndAngle > adjustedStartAngle) {
        paths.push({
          d: this.createUniformGapDonutPath(startAngle, endAngle, sectionOuterRadius, sectionInnerRadius, GAP_PIXELS),
          color: section.color,
          glow: section.glow,
        });
      }

      cumulativePercentage += section.value;
    });

    // Add rail for remaining percentage (this handles the 0% case naturally)
    if (cumulativePercentage < 100) {
      const startAngle = (cumulativePercentage / 100) * 360;
      const endAngle = 360;

      // For rail: use the same gap calculation method as sections for consistency
      paths.push({
        d: this.createUniformGapDonutPath(startAngle, endAngle, railOuterRadius, railInnerRadius, GAP_PIXELS),
        color: this.railColor,
      });
    }

    return paths;
  }

  private validateSections(): void {
    const totalPercentage = this.getTotalSectionValue(this.sections);
    if (totalPercentage > 100) {
      console.warn(`Pie chart sections total ${totalPercentage}%, which exceeds 100%.`);
    }
  }

  private getTotalSectionValue(sections: SgPieChartSection[]): number {
    return sections.reduce((sum, section) => sum + section.value, 0);
  }

  private createUniformGapDonutPath(
    startAngle: number,
    endAngle: number,
    outerRadius: number,
    innerRadius: number,
    gapPixels: number
  ): string {
    // For parallel gap boundaries (straight lines perpendicular to the donut),
    // we need different angular gaps for inner and outer radii
    // Calculate gap angles for both radii to achieve the same linear gap distance
    const outerGapAngle = this.calculateUniformGapAngle(gapPixels, outerRadius);
    const innerGapAngle = this.calculateUniformGapAngle(gapPixels, innerRadius);

    // Apply different gap angles to create straight, parallel gap boundaries
    const halfOuterGap = outerGapAngle / 2;
    const halfInnerGap = innerGapAngle / 2;

    const outerStartAngle = startAngle + halfOuterGap;
    const outerEndAngle = endAngle - halfOuterGap;
    const innerStartAngle = startAngle + halfInnerGap;
    const innerEndAngle = endAngle - halfInnerGap;

    const outerStartAngleRad = (outerStartAngle * Math.PI) / 180;
    const outerEndAngleRad = (outerEndAngle * Math.PI) / 180;
    const innerStartAngleRad = (innerStartAngle * Math.PI) / 180;
    const innerEndAngleRad = (innerEndAngle * Math.PI) / 180;

    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    // Use dynamic center based on size input
    const center = this.size / 2;

    const x1 = center + outerRadius * Math.cos(outerStartAngleRad);
    const y1 = center + outerRadius * Math.sin(outerStartAngleRad);
    const x2 = center + outerRadius * Math.cos(outerEndAngleRad);
    const y2 = center + outerRadius * Math.sin(outerEndAngleRad);

    const x3 = center + innerRadius * Math.cos(innerEndAngleRad);
    const y3 = center + innerRadius * Math.sin(innerEndAngleRad);
    const x4 = center + innerRadius * Math.cos(innerStartAngleRad);
    const y4 = center + innerRadius * Math.sin(innerStartAngleRad);

    return [
      `M ${x1} ${y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ');
  }

  private calculateUniformGapAngle(gapPixels: number, radius: number): number {
    // Calculate the angle that produces the desired straight-line gap distance at the given radius
    // Using the chord length formula: chord = 2 * radius * sin(angle/2)
    // Solving for angle: angle = 2 * arcsin(chord / (2 * radius))

    const halfChord = gapPixels / 2;
    const sinValue = halfChord / radius;

    if (sinValue > 1) {
      console.warn(`Gap of ${gapPixels}px is too large for radius ${radius}px. Using maximum possible angle.`);
      return 180; // Half circle is the maximum
    }

    const gapAngleRadians = 2 * Math.asin(sinValue);
    const gapAngleDegrees = (gapAngleRadians * 180) / Math.PI;

    // Ensure the angle is reasonable (not too large for visual purposes)
    return Math.min(gapAngleDegrees, 10);
  }

  private createDonutPath(startAngle: number, endAngle: number, outerRadius: number, innerRadius: number): string {
    // Handle full circle case (360 degrees) with two semicircles
    if (endAngle - startAngle >= 360) {
      const center = this.size / 2;

      return [
        `M ${center - outerRadius} ${center}`,
        `A ${outerRadius} ${outerRadius} 0 0 1 ${center + outerRadius} ${center}`,
        `A ${outerRadius} ${outerRadius} 0 0 1 ${center - outerRadius} ${center}`,
        `M ${center - innerRadius} ${center}`,
        `A ${innerRadius} ${innerRadius} 0 0 0 ${center + innerRadius} ${center}`,
        `A ${innerRadius} ${innerRadius} 0 0 0 ${center - innerRadius} ${center}`,
        'Z',
      ].join(' ');
    }

    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    // Use dynamic center based on size input
    const center = this.size / 2;

    const getPoint = (center: number, radius: number, angleRad: number): [number, number] => [
      center + radius * Math.cos(angleRad),
      center + radius * Math.sin(angleRad),
    ];

    const [x1, y1] = getPoint(center, outerRadius, startAngleRad);
    const [x2, y2] = getPoint(center, outerRadius, endAngleRad);
    const [x3, y3] = getPoint(center, innerRadius, endAngleRad);
    const [x4, y4] = getPoint(center, innerRadius, startAngleRad);

    return [
      `M ${x1} ${y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ');
  }

  getAnimationState(): string {
    return this.isLoading ? 'loading' : 'loaded';
  }

  hasGlowEffect(): boolean {
    return this.sections?.some((section) => section.glow) || false;
  }

  getSvgSize(): number {
    // Add padding if any section has glow effect
    const glowPadding = this.hasGlowEffect() ? 16 : 0; // 16px padding for glow spread
    return this.size + glowPadding * 2;
  }

  getViewBox(): string {
    const svgSize = this.getSvgSize();
    const offset = this.hasGlowEffect() ? -16 : 0; // Center the original content
    return `${offset} ${offset} ${svgSize} ${svgSize}`;
  }
}
